/**
 * Coquille de l'application : navigation, routage, bandeau réseau.
 *
 * Sur mobile, cinq entrées en bas — il y a plus de cinq sections, donc un choix
 * éditorial était nécessaire : les quatre plus consultées, puis « Plus » pour le
 * reste. Sur desktop, un rail latéral qui les montre toutes.
 */

import { useEffect, useMemo } from 'react';
import {
  Bell,
  CalendarDays,
  CloudOff,
  FileText,
  HeartPulse,
  House,
  Menu,
  Plug,
  RefreshCw,
  ShoppingCart,
  Sun,
  Users,
  ListTodo,
  Wrench,
} from 'lucide-react';

import { joursEntre, aujourdhuiParis } from '@foyer/noyau';

import { regleBarreEtat } from './lib/barre-etat.js';
import { proposeMiseAJour } from './lib/mise-a-jour.js';

import { TRAIT } from './composants/ui.js';
import { useAppli, useRessource, useRoute, navigue } from './lib/appli.js';
import { donnees } from './lib/donnees.js';
import { Agenda } from './vues/Agenda.js';
import { Courses } from './vues/Courses.js';
import { Integrations } from './vues/Integrations.js';
import { Aujourdhui } from './vues/Aujourdhui.js';
import { AssistantEquipement } from './vues/AssistantEquipement.js';
import { Connexion } from './vues/Connexion.js';
import { Famille } from './vues/Famille.js';
import { DetailEquipement, Maison } from './vues/Maison.js';
import { Notifications } from './vues/Notifications.js';
import { FrisePersonne, Sante } from './vues/Sante.js';
import { Administratif, Travaux } from './vues/Listes.js';
import { TempsLibre } from './vues/TempsLibre.js';

interface Entree {
  chemin: string;
  libelle: string;
  Icone: typeof Sun;
  /** Présente dans la barre du bas ; les autres vivent sous « Plus ». */
  principale?: boolean;
  /**
   * Teinte de l'onglet quand il est actif. Toute la barre partageait la même
   * couleur d'accent : on savait qu'un onglet était sélectionné, pas lequel au
   * premier regard. Une teinte par section se retient en deux jours, et rend la
   * barre nettement plus vivante.
   */
  teinte?: string;
}

/**
 * Ordre de la barre du bas : les courses y remplacent l'agenda.
 *
 * L'agenda était en deuxième position par symétrie avec un calendrier
 * classique, mais ce n'est pas ce qu'on ouvre plusieurs fois par jour. La liste
 * de courses, si — et on la consulte debout dans un rayon, là où fouiller un
 * menu coûte cher. L'agenda reste à un geste, sous « Plus ».
 */
const ENTREES: Entree[] = [
  { chemin: '/aujourdhui', libelle: 'Aujourd’hui', Icone: Sun, principale: true, teinte: 'var(--cat-chauffage)' },
  { chemin: '/courses', libelle: 'Courses', Icone: ShoppingCart, principale: true, teinte: 'var(--cat-courses)' },
  /*
   * Trois entrées principales au lieu de quatre : chaque onglet passe de 20 à
   * 25 % de la largeur. « Quand j'ai le temps » ne tenait pas sur une ligne
   * d'onglet à cette taille — l'écran garde la phrase en chapeau, l'onglet
   * prend le nom court.
   */
  {
    chemin: '/temps-libre',
    libelle: 'À faire',
    Icone: ListTodo,
    principale: true,
    teinte: 'var(--cat-assainissement)',
  },
  { chemin: '/maison', libelle: 'Maison', Icone: House, teinte: 'var(--cat-maison)' },
  { chemin: '/sante', libelle: 'Santé', Icone: HeartPulse, teinte: 'var(--cat-sante)' },
  { chemin: '/agenda', libelle: 'Agenda', Icone: CalendarDays, teinte: 'var(--cat-ecole)' },
  { chemin: '/administratif', libelle: 'Administratif', Icone: FileText, teinte: 'var(--cat-administratif)' },
  { chemin: '/travaux', libelle: 'Travaux', Icone: Wrench, teinte: 'var(--cat-travaux)' },
  { chemin: '/famille', libelle: 'Famille', Icone: Users, teinte: 'var(--cat-sante)' },
  { chemin: '/notifications', libelle: 'Notifications', Icone: Bell, teinte: 'var(--cat-assainissement)' },
  { chemin: '/integrations', libelle: 'Intégrations', Icone: Plug, teinte: 'var(--cat-maison)' },
];

export function App() {
  const { chargement, utilisateur, enLigne, enAttente, synchroniseMaintenant } = useAppli();
  const segments = useRoute();

  /**
   * Mise à jour au lancement.
   *
   * Play met bien à jour tout seul, mais à son rythme : une version publiée le
   * soir peut n'arriver que le lendemain. Pour une application livrée plusieurs
   * fois par jour, on se retrouve à regarder un écran corrigé depuis des heures
   * en croyant que le correctif n'est pas parti. On demande donc à Play, à
   * chaque ouverture, et on déclenche son écran de mise à jour.
   *
   * Sans effet sur le web, et silencieux en cas d'échec : une mise à jour ratée
   * ne doit jamais empêcher d'ouvrir l'application.
   */
  useEffect(() => {
    void proposeMiseAJour();
    // La barre d’état se règle au lancement : sans cela le contenu passe sous
    // l’horloge du téléphone.
    void regleBarreEtat();
  }, []);

  if (chargement) {
    return (
      <main className="contenu">
        <div className="chargement">Ouverture de Foyer…</div>
      </main>
    );
  }

  if (!utilisateur) return <Connexion />;

  return (
    <div className="appli">
      <Rail actif={`/${segments[0] ?? 'aujourdhui'}`} />

      <div className="coquille">
        {!enLigne && (
          <div className="bandeau-reseau">
            <CloudOff strokeWidth={TRAIT} size={16} aria-hidden />
            Hors ligne — consultation et cochage restent possibles.
          </div>
        )}
        {enLigne && enAttente.length > 0 && (
          <div className="bandeau-reseau">
            <RefreshCw strokeWidth={TRAIT} size={16} aria-hidden />
            {enAttente.length} action(s) à synchroniser.
            <button type="button" className="bouton texte" onClick={() => void synchroniseMaintenant()}>
              Envoyer maintenant
            </button>
          </div>
        )}

        <main className="contenu">
          <Routes segments={segments} />
        </main>
      </div>

      <NavBasse actif={`/${segments[0] ?? 'aujourdhui'}`} />
    </div>
  );
}

function Routes({ segments }: { segments: string[] }) {
  const [section, parametre] = segments;

  switch (section) {
    case undefined:
    case 'aujourdhui':
      return <Aujourdhui />;
    case 'agenda':
      return <Agenda />;
    case 'maison':
      if (parametre === 'nouveau') return <AssistantEquipement />;
      if (parametre) return <DetailEquipement id={parametre} />;
      return <Maison />;
    case 'sante':
      if (parametre) return <FrisePersonne id={parametre} />;
      return <Sante />;
    case 'travaux':
      return <Travaux />;
    case 'temps-libre':
      return <TempsLibre />;
    case 'administratif':
      return <Administratif />;
    case 'famille':
      return <Famille />;
    case 'courses':
      return <Courses />;
    case 'integrations':
      return <Integrations />;
    case 'notifications':
      return <Notifications />;
    case 'plus':
      return <Plus />;
    default:
      return (
        <>
          <h1>Page inconnue</h1>
          <p className="chapeau">Cette adresse ne correspond à aucun écran.</p>
          <button type="button" className="bouton" onClick={() => navigue('/aujourdhui')}>
            Revenir à aujourd’hui
          </button>
        </>
      );
  }
}

/** Menu « Plus » : les sections qui ne tiennent pas dans la barre du bas. */
function Plus() {
  return (
    <>
      <div className="entete-vue">
        <h1>
          Plus
        </h1>
      </div>
      {ENTREES.filter((e) => !e.principale).map(({ chemin, libelle, Icone, teinte }) => (
        <button
          key={chemin}
          type="button"
          className="carte entree-plus"
          style={teinte ? ({ ['--teinte']: teinte } as React.CSSProperties) : undefined}
          onClick={() => navigue(chemin)}
        >
          <Icone strokeWidth={TRAIT} size={22} aria-hidden />
          <strong>{libelle}</strong>
        </button>
      ))}
    </>
  );
}

function NavBasse({ actif }: { actif: string }) {
  const principales = ENTREES.filter((e) => e.principale);
  const dansPlus = ENTREES.some((e) => !e.principale && e.chemin === actif) || actif === '/plus';

  return (
    <nav className="nav-basse" aria-label="Navigation principale">
      {principales.map(({ chemin, libelle, Icone, teinte }) => (
        <a
          key={chemin}
          href={`#${chemin}`}
          className={actif === chemin ? 'actif' : ''}
          style={teinte ? ({ ['--teinte']: teinte } as React.CSSProperties) : undefined}
        >
          <span className="pastille-nav">
            <Icone strokeWidth={TRAIT} size={19} aria-hidden />
          </span>
          {libelle}
        </a>
      ))}
      <a href="#/plus" className={dansPlus ? 'actif' : ''}>
        <span className="pastille-nav">
          <Menu strokeWidth={TRAIT} size={19} aria-hidden />
        </span>
        Plus
      </a>
    </nav>
  );
}

function Rail({ actif }: { actif: string }) {
  const { utilisateur, deconnecte } = useAppli();
  const aujourdhui = aujourdhuiParis();
  const taches = useRessource(() => donnees.taches({ statut: 'active' }));

  const aFaire = useMemo(() => {
    const liste = taches.donnee?.taches ?? [];
    return liste.filter((t) => t.echeance && joursEntre(t.echeance, aujourdhui) >= 0).length;
  }, [taches.donnee, aujourdhui]);

  return (
    <nav className="rail" aria-label="Navigation">
      <div className="marque">
        <span className="carre">
          <House strokeWidth={TRAIT} size={20} aria-hidden />
        </span>
        <span className="nom">Foyer</span>
      </div>

      {ENTREES.map(({ chemin, libelle, Icone, teinte }) => (
        <a
          key={chemin}
          href={`#${chemin}`}
          className={actif === chemin ? 'actif' : ''}
          style={teinte ? ({ ['--teinte']: teinte } as React.CSSProperties) : undefined}
        >
          <Icone strokeWidth={TRAIT} size={19} aria-hidden />
          {libelle}
          {chemin === '/aujourdhui' && aFaire > 0 && <span className="compte-nav">{aFaire}</span>}
        </a>
      ))}

      <div className="pied">
        <span className="avatar">{utilisateur?.prenom.slice(0, 1).toUpperCase()}</span>
        <span style={{ flex: 1, minWidth: 0 }}>{utilisateur?.prenom}</span>
        <button type="button" className="bouton texte" onClick={() => void deconnecte()}>
          Sortir
        </button>
      </div>
    </nav>
  );
}
